import { useState } from "react";
import { useSearchParams } from "react-router";

import { OrganizationPicker } from "../components/OrganizationPicker.js";
import { Empty, Failure, Panel } from "../components/Panel.js";
import { useResource } from "../hooks.js";
import { apiPost } from "../lib/api.js";
import { type Membership, type Organization } from "../types.js";

const ROLES = ["owner", "administrator", "operator"] as const;

export function MembersPage() {
  const organizations = useResource<Organization[]>(
    "/app/control/organization",
  );
  const [params, setParams] = useSearchParams();
  const organization = params.get("organization") || undefined;
  const path = organization
    ? `/app/control/organization/${organization}/membership`
    : null;
  const members = useResource<Membership[]>(path);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Membership["role"]>("owner");
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setFailure(undefined);
    try {
      await work();
      members.reload();
    } catch (error) {
      setFailure((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <OrganizationPicker
        organizations={organizations.value || []}
        value={organization}
        onChange={(id) => setParams({ organization: id })}
      />

      {!organization ? <Empty>Choose an organization</Empty> : null}

      {organization ? (
        <>
          <Panel title="Invite a user">
            <form
              className="flex flex-wrap gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!email.trim() || !path) return;
                act(async () => {
                  await apiPost(path, { email: email.trim(), role });
                  setEmail("");
                });
              }}
            >
              <input
                value={email}
                type="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="person@example.com"
                className="flex-1 rounded border border-black/15 px-3 py-2"
              />
              <select
                value={role}
                onChange={(event) =>
                  setRole(event.target.value as Membership["role"])
                }
                className="rounded border border-black/15 px-3 py-2"
              >
                {ROLES.map((candidate) => (
                  <option key={candidate} value={candidate}>
                    {candidate}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="rounded bg-jay px-4 py-2 font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                Invite
              </button>
            </form>
            {failure ? (
              <div className="mt-4">
                <Failure>{failure}</Failure>
              </div>
            ) : null}
          </Panel>

          <Panel title="Members">
            {members.loading ? <Empty>Loading</Empty> : null}
            {members.error ? <Failure>{members.error.message}</Failure> : null}
            {members.value && members.value.length === 0 ? (
              <Empty>No members yet</Empty>
            ) : null}
            {members.value && members.value.length > 0 ? (
              <table className="w-full text-left text-sm">
                <thead className="text-ink/50">
                  <tr>
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">Role</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {members.value.map((member) => (
                    <tr key={member.id} className="border-t border-black/5">
                      <td className="py-2">{member.email}</td>
                      <td className="py-2">
                        <select
                          value={member.role}
                          disabled={busy}
                          onChange={(event) =>
                            act(() =>
                              apiPost(`${path}/role`, {
                                email: member.email,
                                role: event.target.value,
                              }),
                            )
                          }
                          className="rounded border border-black/15 px-2 py-1"
                        >
                          {ROLES.map((candidate) => (
                            <option key={candidate} value={candidate}>
                              {candidate}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2">{member.status}</td>
                      <td className="py-2 text-right">
                        {member.status !== "revoked" ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              act(() =>
                                apiPost(`${path}/revoke`, {
                                  email: member.email,
                                }),
                              )
                            }
                            className="text-red-700 hover:underline"
                          >
                            Revoke
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </Panel>
        </>
      ) : null}
    </>
  );
}
